import classNames from 'classnames';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert, { ALERT_TYPES } from '../../components/atoms/Alert';
import TeamManagementAPI from '../service';
import { PAGE_MODES, translationPrefix } from '../utils';
import FormElement from './FormElement';
import StepCard from './StepCard';
import css from './UserDetailsPageStep.css';

const EMAIL_REGEX = /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;

const formPrefix = `${translationPrefix}.MANAGE_PERMISSIONS_PAGE.USER_DETAILS_FORM`;

const UserDetailsPageStep = ({
    userRegistrationForm,
    mode = '',
    username = '',
    saveMemberError = '',
    onFieldsUpdated = () => {},
    onStepValidationStatusChanged = () => {},
    translate = () => {},
}) => {
    const [fields, setFields] = useState({
        firstName: {
            value: '',
            label: translate(`${formPrefix}.FIRST_NAME`),
            optionalText: translate(`${formPrefix}.OPTIONAL`),
            valid: true,
            dirty: false,
            maxLength: 20,
            ...userRegistrationForm.firstName,
        },
        lastName: {
            value: '',
            label: translate(`${formPrefix}.LAST_NAME`),
            optionalText: translate(`${formPrefix}.OPTIONAL`),
            valid: true,
            dirty: false,
            maxLength: 20,
            ...userRegistrationForm.lastName,
        },
        email: {
            value: '',
            label: translate(`${formPrefix}.EMAIL`),
            valid: false,
            dirty: false,
            errorMessage: '',
            ...userRegistrationForm.email,
        },
        description: {
            value: '',
            label: translate(`${formPrefix}.DESCRIPTION_LABEL`),
            optionalText: translate(`${formPrefix}.DESCRIPTION_HINT`),
            placeholder: translate(`${formPrefix}.DESCRIPTION_PLACEHOLDER`),
            valid: true,
            dirty: false,
            maxLength: 30,
            ...userRegistrationForm.description,
        },
    });

    const [showExternalUserWarning, setShowExternalUserWarning] = useState(false);
    const [showExistingEmailWarning, setShowExistingEmailWarning] = useState(false);
    const [existingEmailMsg, setExistingEmailMsg] = useState('');
    const isEdit = mode === PAGE_MODES.edit;

    const debouncedUpdate = useRef(
        debounce(fieldsObj => {
            onFieldsUpdated(fieldsObj);
        }, 500)
    ).current;

    const debouncedEmailCheck = useRef(
        debounce((value, fieldsSetter, currentMode) => {
            if (currentMode !== PAGE_MODES.edit) {
                new TeamManagementAPI()
                    .checkUserExists(value)
                    .then(response => {
                        fieldsSetter(prev => ({
                            ...prev,
                            email: {
                                ...prev.email,
                                dirty: prev.email.dirty || (!!value && response.exists),
                            },
                        }));
                        setShowExistingEmailWarning(response.exists);
                        setExistingEmailMsg(response.msg || '');
                    })
                    .catch(() => {
                        setShowExistingEmailWarning(true);
                        setExistingEmailMsg(translate(`${formPrefix}.EMAIL_CHECK_FAILED`));
                    });
            }
        }, 500)
    ).current;

    const debouncedRunValidations = useRef(
        debounce((emailValue, validateFn) => {
            validateFn(emailValue);
        }, 500)
    ).current;

    useEffect(() => {
        return () => {
            debouncedUpdate.flush();
            debouncedEmailCheck.cancel();
            debouncedRunValidations.cancel();
        };
    }, [debouncedUpdate, debouncedEmailCheck, debouncedRunValidations]);

    const isEmailDifferentDomain = useCallback(
        email => {
            const domainStartIndex = username.indexOf('@');
            if (domainStartIndex === -1) return false;
            const domainRegex = new RegExp(
                `^[A-Za-z0-9._%+-]+${username.substring(domainStartIndex).replace('.', '\\.')}$`
            );
            return !domainRegex.test(email);
        },
        [username]
    );

    useEffect(() => {
        const isValid = fields.email.valid && !showExistingEmailWarning;
        let title = '';
        if (!isValid) {
            title = showExistingEmailWarning ? existingEmailMsg : translate(`${translationPrefix}.FILL_FIELDS_TOOLTIP`);
        }
        onStepValidationStatusChanged({ isValid, title });
    }, [fields.email.valid, showExistingEmailWarning, existingEmailMsg, onStepValidationStatusChanged, translate]);

    useEffect(() => {
        if (fields.email.value && EMAIL_REGEX.test(fields.email.value)) {
            debouncedEmailCheck(fields.email.value, setFields, mode);
        }
    }, [fields.email.value, debouncedEmailCheck, setFields, mode]);

    const getFieldsObject = currentFields => {
        const keys = ['firstName', 'lastName', 'email', 'description'];
        return keys.reduce((acc, key) => {
            acc[key] = {
                value: currentFields[key].value,
                dirty: currentFields[key].dirty,
                valid: currentFields[key].valid,
            };
            return acc;
        }, {});
    };

    const validateEmail = useCallback(
        (value, forceDirty = false) => {
            const valid = EMAIL_REGEX.test(value);
            const newExternalWarning = valid && isEmailDifferentDomain(value);

            setFields(prev => {
                const updated = {
                    ...prev,
                    email: {
                        ...prev.email,
                        value,
                        valid,
                        dirty: prev.email.dirty || (!!value && valid) || forceDirty,
                        errorMessage: valid
                            ? ''
                            : value
                            ? translate(`${formPrefix}.INVALID_EMAIL`)
                            : translate(`${formPrefix}.MANDATORY_FIELD`),
                    },
                };
                debouncedUpdate(getFieldsObject(updated));
                return updated;
            });

            setShowExternalUserWarning(newExternalWarning);

            if (valid && mode !== PAGE_MODES.edit) {
                debouncedEmailCheck(value, setFields, mode);
            } else {
                setShowExistingEmailWarning(false);
            }
        },
        [debouncedEmailCheck, setFields, translate, debouncedUpdate, isEmailDifferentDomain, mode]
    );

    useEffect(() => {
        if (fields.email.value) {
            debouncedRunValidations(fields.email.value, validateEmail);
        }
    }, [fields.email.value, debouncedRunValidations, validateEmail]);

    const onFieldChange = (fieldId, value) => {
        if (fieldId === 'email') {
            validateEmail(value);
        } else {
            setFields(prev => {
                const updated = { ...prev, [fieldId]: { ...prev[fieldId], value } };
                debouncedUpdate(getFieldsObject(updated));
                return updated;
            });
        }
    };

    const onFieldBlur = (fieldId, value) => {
        if (fieldId === 'email') {
            validateEmail(value, true);
        }
    };

    return (
        <div className={css.container}>
            {showExternalUserWarning && !saveMemberError && (
                <Alert
                    type={ALERT_TYPES.WARNING}
                    text={`${translationPrefix}.EXTERNAL_USER_WARNING`}
                    className={classNames(css.alert, css.warning)}
                    alertIconClassName={css.alertIcon}
                />
            )}
            {showExistingEmailWarning && existingEmailMsg && (
                <Alert
                    type={ALERT_TYPES.ERROR}
                    text={existingEmailMsg}
                    className={classNames(css.alert, css.error)}
                    alertIconClassName={css.alertIcon}
                />
            )}
            {saveMemberError && (
                <Alert
                    type={ALERT_TYPES.ERROR}
                    text={saveMemberError}
                    className={classNames(css.alert, css.error)}
                    alertIconClassName={css.alertIcon}
                />
            )}
            <StepCard
                className={css.stepCard}
                title={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.TITLE.USER_DETAILS`}
            >
                <div className={css.formGrid}>
                    <FormElement
                        disabled={isEdit}
                        label={fields.email.label}
                        value={fields.email.value}
                        valid={fields.email.valid}
                        dirty={fields.email.dirty}
                        errorMessage={fields.email.errorMessage}
                        onChange={val => onFieldChange('email', val)}
                        onBlur={val => onFieldBlur('email', val)}
                    />
                    <div className={css.nameRow}>
                        <FormElement
                            disabled={isEdit}
                            label={fields.firstName.label}
                            value={fields.firstName.value}
                            optionalText={fields.firstName.optionalText}
                            maxLength={fields.firstName.maxLength}
                            onChange={val => onFieldChange('firstName', val)}
                        />
                        <FormElement
                            disabled={isEdit}
                            label={fields.lastName.label}
                            value={fields.lastName.value}
                            optionalText={fields.lastName.optionalText}
                            maxLength={fields.lastName.maxLength}
                            onChange={val => onFieldChange('lastName', val)}
                        />
                    </div>
                    <FormElement
                        label={fields.description.label}
                        value={fields.description.value}
                        optionalText={fields.description.optionalText}
                        placeholder={fields.description.placeholder}
                        maxLength={fields.description.maxLength}
                        onChange={val => onFieldChange('description', val)}
                    />
                </div>
            </StepCard>
        </div>
    );
};

const fieldShape = PropTypes.shape({
    value: PropTypes.string,
    valid: PropTypes.bool,
    dirty: PropTypes.bool,
});

UserDetailsPageStep.propTypes = {
    userRegistrationForm: PropTypes.shape({
        firstName: fieldShape,
        lastName: fieldShape,
        email: fieldShape,
        description: fieldShape,
    }).isRequired,
    mode: PropTypes.string,
    username: PropTypes.string,
    saveMemberError: PropTypes.string,
    onFieldsUpdated: PropTypes.func,
    onStepValidationStatusChanged: PropTypes.func,
    translate: PropTypes.func,
};

export default UserDetailsPageStep;
